/**
 * DocumentPreviewModal — production-grade secure document viewer.
 *
 * SECURITY MODEL
 * ──────────────
 * • Every byte of every document travels through the authenticated API.
 *   No MinIO URL ever reaches the browser directly for viewing.
 * • Audit record written on every open (stream endpoint logs `read`).
 * • PDF: blob URL in sandboxed iframe (browser's native engine).
 * • DOCX: rendered client-side by docx-preview, then sanitized with
 *   DOMPurify before touching the DOM. External fonts/resources blocked.
 * • Magic bytes validated server-side on upload (rejects spoofed files).
 * • Content-Security-Policy header on the stream response blocks exfil.
 *
 * PANEL
 * ─────
 * • Floating, draggable, 8-direction resizable.
 * • Maximise / restore. Escape or × to close.
 * • Version history sidebar (toggleable, right panel).
 */

import DOMPurify from 'dompurify';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  X,
  DownloadSimple,
  CircleNotch,
  Warning,
  FilePdf,
  FileDoc,
  FileText,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowsOut,
  ArrowsIn,
  ClockCounterClockwise,
  User,
  Trash,
  Headset,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { api, normaliseApiError } from '@/lib/api';
import { deleteDocument } from '@/lib/documentsApi';
import { captureFailureContext, failureFromApiError } from '@/lib/supportApi';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '@/features/auth/store';
import { canDeleteDocument } from '@/lib/pupilUiBoundary';

// ── Geometry ──────────────────────────────────────────────────

const MIN_W = 560;
const MIN_H = 400;
/** Breathing room so the window never sits flush against the edge of the screen. */
const MARGIN = 8;

/**
 * The smallest this window may be made — but never larger than the screen it floats on.
 *
 * `MIN_W` was applied unconditionally. On a 390px phone that produced a 560px window, 170px
 * wider than the viewport, centred at x = (390 − 560) / 2 = −85 — so it opened 85px off the
 * LEFT edge with the filename starting mid-word and the right-hand controls (expand, close)
 * unreachable. A floating window cannot be wider than the thing it floats on.
 */
function floorW() { return Math.min(MIN_W, window.innerWidth - MARGIN * 2); }
function floorH() { return Math.min(MIN_H, window.innerHeight - MARGIN * 2); }

function initialGeo() {
  const maxW = window.innerWidth - MARGIN * 2;
  const maxH = window.innerHeight - MARGIN * 2;
  const w = Math.min(Math.max(window.innerWidth * 0.90, floorW()), Math.min(1300, maxW));
  const h = Math.min(Math.max(window.innerHeight * 0.88, floorH()), maxH);
  return {
    x: Math.max(MARGIN, (window.innerWidth - w) / 2),
    y: Math.max(MARGIN, (window.innerHeight - h) / 2),
    w,
    h,
  };
}

// ── Types ─────────────────────────────────────────────────────

interface Props {
  documentId: string | null;
  documentName?: string;
  onClose: () => void;
  onDeleted?: () => void;
}

type ResizeDir = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
type DocKind   = 'pdf' | 'docx' | 'image' | 'other';

interface ReadyDoc {
  kind: DocKind;
  name: string;
  version?: number;
  /** Blob URL — created from authenticated stream response */
  blobUrl: string;
  /** For DOCX: sanitized HTML to inject via dangerouslySetInnerHTML */
  sanitizedHtml?: string;
  /** DOCX CSS injected by docx-preview — extracted before sanitization */
  docxStyles?: string;
}

interface DocVersion {
  id: string;
  version: number;
  name: string;
  fileSizeBytes: number | null;
  createdAt: string;
  uploadedBy?: { firstName: string; lastName: string };
}

// ── Helpers ───────────────────────────────────────────────────

function detectKind(mime: string): DocKind {
  const m = ((mime ?? '').toLowerCase().split(';')[0] ?? '').trim();
  if (m === 'application/pdf') return 'pdf';
  if (
    m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    m === 'application/msword'
  ) return 'docx';
  if (m.startsWith('image/')) return 'image';
  return 'other';
}

function parseFilename(cd: string | undefined): string | null {
  if (!cd) return null;
  const m = cd.match(/filename="?([^";]+)"?/);
  return m?.[1] ? decodeURIComponent(m[1].trim()) : null;
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── DOCX rendering (secure) ───────────────────────────────────

async function renderDocxSecure(data: ArrayBuffer): Promise<{ sanitizedHtml: string; docxStyles: string }> {
  const { renderAsync } = await import('docx-preview');

  // Render into a detached element — never touches the live DOM until sanitized
  const host = document.createElement('div');

  await renderAsync(data, host, undefined, {
    className: 'docx-content',
    inWrapper: true,
    // Security: disable external font loading; use data URIs for embedded images.
    // These two settings prevent any network requests from the rendered output.
    ignoreFonts: true,
    useBase64URL: true,
    ignoreWidth: false,
    ignoreHeight: false,
    breakPages: true,
    experimental: false,
    renderHeaders: true,
    renderFooters: true,
    renderEndnotes: true,
  });

  // Extract injected <style> tags before sanitizing the rest
  const styleEls = Array.from(host.querySelectorAll('style'));
  const docxStyles = styleEls.map(s => s.outerHTML).join('\n');
  styleEls.forEach(s => s.remove()); // remove from body to avoid double injection

  // Sanitize the body HTML.
  // Allows: class, data-* attributes, style attributes (for inline Word formatting).
  // Blocks: scripts, iframes, objects, event handlers, external src/href.
  const rawHtml = host.innerHTML;
  const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
    FORCE_BODY: true,
    ADD_TAGS: ['style'],
    ADD_ATTR: ['class', 'style', 'data-docx-*'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link'],
    FORBID_ATTR: [
      'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
      'onfocus', 'onblur', 'onchange', 'onkeyup', 'onkeydown',
      'xlink:href',
    ],
    ALLOW_DATA_ATTR: true,
  });

  return { sanitizedHtml, docxStyles };
}

// ── Version history panel ─────────────────────────────────────

function VersionPanel({ documentId, currentVersion }: { documentId: string; currentVersion?: number }) {
  const [versions, setVersions] = useState<DocVersion[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api
      .get<{ data: DocVersion[] }>(`/documents/${documentId}/versions`)
      .then(r => setVersions(r.data.data ?? []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [documentId]);

  if (loading) return (
    <div className="flex h-full items-center justify-center">
      <CircleNotch className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );

  if (versions.length === 0) return (
    <div className="p-4 text-center text-xs text-muted-foreground">No version history</div>
  );

  return (
    <div className="flex flex-col divide-y divide-neutral-100 overflow-y-auto">
      {versions.map(v => {
        const isCurrent = v.version === (currentVersion ?? Math.max(...versions.map(x => x.version)));
        const uploader  = v.uploadedBy
          ? `${v.uploadedBy.firstName} ${v.uploadedBy.lastName}`
          : 'Unknown';
        const timeAgo = formatDistanceToNow(new Date(v.createdAt), { addSuffix: true });

        return (
          <div key={v.id}
            className={`px-4 py-3 ${isCurrent ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-muted dark:hover:bg-slate-800/50'}`}>
            <div className="flex items-center justify-between gap-2">
              <span className={`text-xs font-semibold ${isCurrent ? 'text-blue-700 dark:text-blue-400' : 'text-foreground dark:text-slate-300'}`}>
                v{v.version}
                {isCurrent && (
                  <span className="ml-1.5 rounded-full bg-blue-100 dark:bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-400">
                    current
                  </span>
                )}
              </span>
              <span className="text-[11px] text-muted-foreground dark:text-slate-500">{formatBytes(v.fileSizeBytes)}</span>
            </div>
            <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground dark:text-slate-400">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{uploader}</span>
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground dark:text-slate-500">{timeAgo}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Resize handle ─────────────────────────────────────────────

const CURSOR_MAP: Record<ResizeDir, string> = {
  n: 'cursor-n-resize', s: 'cursor-s-resize',
  e: 'cursor-e-resize', w: 'cursor-w-resize',
  ne: 'cursor-ne-resize', nw: 'cursor-nw-resize',
  se: 'cursor-se-resize', sw: 'cursor-sw-resize',
};

function Handle({ dir, onDown }: { dir: ResizeDir; onDown: (d: ResizeDir, e: React.MouseEvent) => void }) {
  const pos: Record<ResizeDir, React.CSSProperties> = {
    n:  { top: 0, left: 14, right: 14, height: 8 },
    s:  { bottom: 0, left: 14, right: 14, height: 8 },
    e:  { right: 0, top: 14, bottom: 14, width: 8 },
    w:  { left: 0, top: 14, bottom: 14, width: 8 },
    ne: { top: 0, right: 0, width: 14, height: 14 },
    nw: { top: 0, left: 0, width: 14, height: 14 },
    se: { bottom: 0, right: 0, width: 14, height: 14 },
    sw: { bottom: 0, left: 0, width: 14, height: 14 },
  };
  return (
    <div className={`absolute z-10 ${CURSOR_MAP[dir]}`} style={pos[dir]}
      onMouseDown={e => onDown(dir, e)} />
  );
}

// ── Main component ────────────────────────────────────────────

export function DocumentPreviewModal({ documentId, documentName, onClose, onDeleted }: Props) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [geo,           setGeo]           = useState(initialGeo);
  const [max,           setMax]           = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [progress,      setProgress]      = useState(0);
  const [error,         setError]         = useState<string | null>(null);
  const [doc,           setDoc]           = useState<ReadyDoc | null>(null);
  const [zoom,          setZoom]          = useState(1);
  const [showHistory,   setShowHistory]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  const blobRef  = useRef<string | null>(null);
  const savedGeo = useRef(geo);
  const panelRef = useRef<HTMLDivElement>(null);
  /** The unzoomed box the document is rendered into — measured to fit a page to its width. */
  const viewRef  = useRef<HTMLDivElement>(null);
  /**
   * The zoom at which a whole page fits across the window, worked out once per document.
   * 1 on anything desktop-sized; the reset control returns here rather than to a flat 100%,
   * which on a phone would mean scrolling sideways again.
   */
  const fitZoom  = useRef(1);

  // ── Free blob URL ────────────────────────────────────────

  const freeBlob = useCallback(() => {
    if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
  }, []);

  // ── Secure document load ─────────────────────────────────
  // All content goes through the authenticated stream endpoint.
  // Audit record written on every access. Signed URLs never used for viewing.

  useEffect(() => {
    if (!documentId) return;
    setGeo(initialGeo());
    setMax(false);
    setLoading(true);
    setProgress(0);
    setError(null);
    setDoc(null);
    setZoom(1);
    fitZoom.current = 1;
    setShowHistory(false);
    freeBlob();

    api
      .get<ArrayBuffer>(`/documents/${documentId}/stream`, {
        responseType: 'arraybuffer',
        onDownloadProgress(e) {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then(async res => {
        const mime = (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream';
        const name = parseFilename(res.headers['content-disposition'] as string | undefined)
          ?? documentName ?? 'Document';
        const kind = detectKind(mime);

        const blob   = new Blob([res.data], { type: mime });
        const blobUrl = URL.createObjectURL(blob);
        blobRef.current = blobUrl;

        if (kind === 'docx') {
          // Security path: render then sanitize before touching the DOM
          const { sanitizedHtml, docxStyles } = await renderDocxSecure(res.data);
          setDoc({ kind, name, blobUrl, sanitizedHtml, docxStyles });
        } else {
          setDoc({ kind, name, blobUrl });
        }
      })
      .catch(err => {
        // Decoded, NOT read straight off `err.response.data`. This request sets
        // `responseType: 'arraybuffer'`, so Axios hands back the error body as bytes and every
        // `data.error.code` read here was silently `undefined` — which meant the three branches
        // below collapsed into the last one and a barrister was told "it may have been removed,
        // or you may not have permission" for a storage outage. See normaliseApiError.
        const info = normaliseApiError(err);
        const status = info.status ?? undefined;
        const code = info.code ?? '';
        const msg = info.message ?? '';
        setError(
          // Read the server's error CODE, never sniff its prose. This branch used to match
          // on the words "not in storage", which a storage OUTAGE also produced — so while
          // MinIO was merely down it told the barrister to delete their document and upload
          // a fresh copy. Destructive advice, about a file that was sitting safely in the
          // bucket the whole time. An outage and a genuinely missing object are different
          // facts and get different answers.
          code === 'STORAGE_UNAVAILABLE' || status === 503
            ? msg || 'Document storage is temporarily unavailable. The document is safe — please try again shortly.'
            // Now a CODE, as the comment above always intended. It used to match the words
            // "not found in storage", which meant the server could not reword its own message
            // without silently changing what the barrister was told.
            : code === 'DOCUMENT_FILE_MISSING'
              ? msg || 'This file is missing from storage. Please ask your clerk to restore it, or report a problem below.'
              : status === 404
                ? 'This document is not available — it may have been removed, or you may not have permission to view it.'
                : 'The document could not be loaded. Please try again or contact your clerk.',
        );

        // Remember WHAT failed, so "Report a problem" below can hand support the exact request
        // instead of a description. Captured here — at the surface that actually showed the
        // user a failure — rather than in a global interceptor, because a background poll that
        // errors is not something the user is complaining about.
        captureFailureContext({
          ...failureFromApiError(err),
          target: { type: 'Document', id: documentId },
        });
      })
      .finally(() => setLoading(false));

    return freeBlob;
  }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard: ESC + Ctrl zoom ────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (!e.ctrlKey) return;
      if (['=', '+', '-', '0'].includes(e.key)) {
        e.preventDefault();
        if (doc?.kind === 'pdf') return; // browser iframe handles PDF zoom natively
        if (e.key === '+' || e.key === '=') setZoom(z => Math.min(3, +(z + 0.15).toFixed(2)));
        else if (e.key === '-')              setZoom(z => Math.max(0.3, +(z - 0.15).toFixed(2)));
        else                                 setZoom(fitZoom.current);
      }
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose, doc?.kind]);

  // Block browser viewport zoom while panel is open
  useEffect(() => {
    if (!documentId) return;
    const block = (e: WheelEvent) => { if (e.ctrlKey) e.preventDefault(); };
    window.addEventListener('wheel', block, { passive: false });
    return () => window.removeEventListener('wheel', block);
  }, [documentId]);

  // Ctrl+scroll → zoom for DOCX / image (PDF handles its own)
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !doc || doc.kind === 'pdf') return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(z => Math.max(0.3, Math.min(3, +(z + (e.deltaY < 0 ? 0.1 : -0.1)).toFixed(2))));
    };
    panel.addEventListener('wheel', onWheel, { passive: false });
    return () => panel.removeEventListener('wheel', onWheel);
  }, [doc?.kind]);

  // ── Fit a DOCX page to the width of the window ───────────────
  //
  // A Word page is a fixed physical width — A4 renders at ~794 CSS px, US Letter at 816 — and
  // docx-preview emits exactly that, because that is what makes a preview faithful. On a phone
  // the window is ~350px wide, so the page overhung it by more than its own width again. The
  // wrapper centres its pages, and a centred flex child that overflows is unreachable on the
  // LEFT at any scroll position: the first two or three characters of every line were simply
  // gone, which is what made the preview unusable rather than merely small.
  //
  // So measure the page once, at natural size, and open at the zoom where it fits across the
  // window. Zoom is real scaling, not reflow — the document still looks like the document, and
  // the existing +/− controls and Ctrl+scroll still work from there. Desktop is untouched: a
  // 1300px window already holds a page at 100%, so the measurement returns 1 and nothing moves.
  useLayoutEffect(() => {
    if (doc?.kind !== 'docx' || doc.sanitizedHtml === undefined) return;
    const view    = viewRef.current;
    // Keyed off the PAGE, not off a class name. This looked for `.docx-wrapper`, which is what
    // docx-preview's own documentation calls the container — but what arrives here, through the
    // sanitizer, is `.docx-content-wrapper` holding `section.docx-content`. The selector matched
    // nothing, so this effect returned on its first line and the preview opened at 100% on a
    // phone: an 816px US-Letter page in a 372px window, cut off with no way to read it. A page
    // is always a <section> whatever the wrapper is called, and its parent is the wrapper.
    const page    = view?.querySelector<HTMLElement>('section');
    const wrapper = page?.parentElement;
    if (!view || !wrapper || !page) return;
    // Measured while zoom is 1 — the loader resets it on every open — so these are the page's
    // natural dimensions. The room a page has is the wrapper's content box: its own padding is
    // read rather than assumed, because that padding tightens below 640px.
    const pad       = getComputedStyle(wrapper);
    const available = wrapper.clientWidth - parseFloat(pad.paddingLeft) - parseFloat(pad.paddingRight);
    const natural   = page.getBoundingClientRect().width;
    if (natural <= 0 || available <= 0 || natural <= available) return;
    // Floor to a whole percent so the readout is not 47.3%, and never below the 30% the zoom
    // controls themselves allow.
    const fit = Math.max(0.3, Math.floor((available / natural) * 100) / 100);
    fitZoom.current = fit;
    setZoom(fit);
  }, [doc]);

  // ── Drag to move (pointer capture — reliable even on fast moves) ──

  const startDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a,button,[data-nodrag]') || max) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
    const ox = e.clientX - geo.x, oy = e.clientY - geo.y;
    const onMove = (me: PointerEvent) => {
      setGeo(g => ({
        ...g,
        x: Math.max(0, Math.min(window.innerWidth  - g.w, me.clientX - ox)),
        y: Math.max(0, Math.min(window.innerHeight - g.h, me.clientY - oy)),
      }));
    };
    const onUp = () => {
      el.style.cursor = '';
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup',   onUp);
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup',   onUp);
  }, [geo.x, geo.y, max]);

  // ── Drag to resize ───────────────────────────────────────

  const startResize = useCallback((dir: ResizeDir, e: React.MouseEvent) => {
    if (max) return;
    e.preventDefault(); e.stopPropagation();
    const { clientX: sx, clientY: sy } = e;
    const { x, y, w, h } = geo;
    const move = (me: MouseEvent) => {
      const dx = me.clientX - sx, dy = me.clientY - sy;
      setGeo(g => {
        let nx = g.x, ny = g.y, nw = g.w, nh = g.h;
        // Same screen-aware floor as initialGeo: on a narrow screen MIN_W would otherwise make
        // the window un-shrinkable below the viewport width.
        if (dir.includes('e')) nw = Math.max(floorW(), w + dx);
        if (dir.includes('s')) nh = Math.max(floorH(), h + dy);
        if (dir.includes('w')) { nw = Math.max(floorW(), w - dx); nx = x + w - nw; }
        if (dir.includes('n')) { nh = Math.max(floorH(), h - dy); ny = y + h - nh; }
        return { x: nx, y: ny, w: nw, h: nh };
      });
    };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, [geo, max]);

  // ── Maximise / restore ───────────────────────────────────

  const toggleMax = () => {
    if (!max) { savedGeo.current = geo; setGeo({ x: 0, y: 0, w: window.innerWidth, h: window.innerHeight }); }
    else setGeo(savedGeo.current);
    setMax(m => !m);
  };

  if (!documentId) return null;

  const isPdf   = doc?.kind === 'pdf';
  const isDocx  = doc?.kind === 'docx';
  const isImage = doc?.kind === 'image';
  const showZoom = (isDocx || isImage) && !loading && !error;

  const panelStyle: React.CSSProperties = max
    ? { position: 'fixed', inset: 0, borderRadius: 0 }
    : { left: geo.x, top: geo.y, width: geo.w, height: geo.h, borderRadius: 10 };

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      <div
        ref={panelRef}
        className="absolute flex flex-col overflow-hidden bg-card pointer-events-auto"
        style={{ border: '1px solid #d4d4d4', boxShadow: '0 8px 40px rgba(0,0,0,.28)', ...panelStyle }}
      >
        {/* ── Resize handles ── */}
        {!max && (['n','ne','e','se','s','sw','w','nw'] as ResizeDir[]).map(d => (
          <Handle key={d} dir={d} onDown={startResize} />
        ))}

        {/* ── Title bar ── */}
        {/* Wraps onto a second row when the controls do not fit, rather than running them off the
            edge. There are nine of them — zoom out/reset/in, history, download, maximise, delete,
            close — and on a phone-width window the last of those fell outside the frame, CLOSE
            among them, so the window could be opened and not dismissed. Unchanged from `sm` up,
            where they fit on one row. */}
        <div
          className="flex min-h-10 shrink-0 select-none flex-wrap items-center justify-between gap-y-1 border-b border-border bg-muted px-3 py-1 sm:h-10 sm:flex-nowrap sm:py-0"
          style={{ cursor: max ? 'default' : 'grab' }}
          onPointerDown={startDrag}
        >
          {/* Left: icon + filename */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {isPdf  && <FilePdf  className="h-4 w-4 shrink-0 text-red-500"  weight="fill" />}
            {isDocx && <FileDoc  className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" weight="fill" />}
            {!isPdf && !isDocx && <FileText className="h-4 w-4 shrink-0 text-muted-foreground" weight="duotone" />}
            <span className="truncate text-[13px] font-semibold text-foreground">
              {/* 'Loading…' only while actually loading. The error state left the title reading
                  "Loading…" above a "Cannot open document" panel — two contradictory statements
                  on one screen, which is exactly what makes a user distrust the rest of it. */}
              {doc?.name ?? documentName ?? (loading ? 'Loading…' : 'Document')}
            </span>
          </div>

          {/* Right: controls */}
          {/* The nine controls measure 421px — wider than the 372px bar on a phone — so the group
              wraps INTERNALLY. Wrapping only the bar around it did nothing: a single flex item
              cannot wrap within itself, so Delete and Close stayed outside the frame. */}
          <div
            className="flex flex-wrap items-center justify-end gap-0.5 sm:ml-2 sm:shrink-0 sm:flex-nowrap"
            data-nodrag
          >
            {/* DOCX / Image zoom */}
            {showZoom && <>
              <button type="button" onMouseDown={e => e.stopPropagation()}
                onClick={() => setZoom(z => Math.max(0.3, +(z - 0.15).toFixed(2)))}
                className="cursor-pointer flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                title="Zoom out (Ctrl −)">
                <MagnifyingGlassMinus className="h-3.5 w-3.5" />
              </button>
              <button type="button" onMouseDown={e => e.stopPropagation()} onClick={() => setZoom(fitZoom.current)}
                className="cursor-pointer flex h-7 min-w-[3rem] items-center justify-center rounded px-1 text-xs tabular-nums text-muted-foreground hover:bg-muted"
                title="Reset (Ctrl 0)">
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" onMouseDown={e => e.stopPropagation()}
                onClick={() => setZoom(z => Math.min(3, +(z + 0.15).toFixed(2)))}
                className="cursor-pointer flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                title="Zoom in (Ctrl +)">
                <MagnifyingGlassPlus className="h-3.5 w-3.5" />
              </button>
              <div className="mx-1 h-4 w-px bg-border" />
            </>}

            {/* Version history toggle */}
            {doc && (
              <button type="button" onMouseDown={e => e.stopPropagation()}
                onClick={() => setShowHistory(h => !h)}
                className={`cursor-pointer flex h-7 items-center gap-1.5 rounded px-2 text-[12px] font-medium ml-0.5
                  ${showHistory ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted'}`}
                title="Version history">
                <ClockCounterClockwise className="h-3.5 w-3.5" />
                History
              </button>
            )}

            {/* Download via blob URL — separate from viewer, does not use signed URL */}
            {doc?.blobUrl && (
              <a href={doc.blobUrl} download={doc.name}
                onMouseDown={e => e.stopPropagation()}
                className="cursor-pointer flex h-7 items-center gap-1.5 rounded border border-border bg-card px-2 text-[12px] font-medium text-foreground hover:bg-muted ml-0.5">
                <DownloadSimple className="h-3.5 w-3.5" />
                Download
              </a>
            )}

            {/* Maximise */}
            <button type="button" onMouseDown={e => e.stopPropagation()} onClick={toggleMax}
              className="cursor-pointer flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground ml-0.5"
              title={max ? 'Restore' : 'Maximise'}>
              {max ? <ArrowsIn className="h-3.5 w-3.5" /> : <ArrowsOut className="h-3.5 w-3.5" />}
            </button>

            {/* Delete */}
            <div className="ml-1 flex items-center" onMouseDown={e => e.stopPropagation()}>
              {/* Pupils hold document create + update but NOT `document.delete`, so the control is
                  omitted for them entirely rather than offered and then 403'd. */}
              {!canDeleteDocument(user?.memberType) ? null : confirmDelete ? (
                <span className="flex items-center gap-1 rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-2 py-1">
                  <span className="text-[11px] font-semibold text-red-700 dark:text-red-300">Delete?</span>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={async () => {
                      if (!documentId) return;
                      setDeleting(true);
                      try {
                        await deleteDocument(documentId);
                        toast.success('Document deleted');
                        onDeleted?.();
                        onClose();
                      } catch {
                        toast.error('Could not delete — please try again');
                        setConfirmDelete(false);
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    className="text-[11px] font-bold text-red-700 dark:text-red-300 hover:underline disabled:opacity-50"
                  >
                    {deleting ? 'Deleting…' : 'Yes'}
                  </button>
                  <span className="text-muted-foreground/60">·</span>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="text-[11px] text-muted-foreground hover:underline"
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="cursor-pointer flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete document"
                >
                  <Trash className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Close */}
            <button type="button" onMouseDown={e => e.stopPropagation()} onClick={onClose}
              className="cursor-pointer flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400 ml-0.5"
              title="Close (Esc)">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Body: content + optional history sidebar ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Document area ── */}
          <div className="relative flex-1 overflow-hidden">

            {/* Loading */}
            {loading && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <CircleNotch className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
                <p className="text-sm text-muted-foreground">
                  {progress > 0 && progress < 100 ? `Loading — ${progress}%` : 'Opening document…'}
                </p>
                {progress > 0 && progress < 100 && (
                  <div className="h-1 w-52 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-blue-600 transition-all duration-150"
                      style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
                <Warning className="h-10 w-10 text-amber-500" weight="duotone" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Cannot open document</p>
                  <p className="mt-1 max-w-sm text-xs text-muted-foreground leading-relaxed">{error}</p>
                </div>
                {/*
                  A dead end is what destroys trust (Hard Rule #22). The user has just been told
                  the thing they need will not open; the next thing they see must be a way to get
                  help, pre-loaded with the technical context they could not possibly supply.
                */}
                <button
                  type="button"
                  onClick={() => { onClose(); navigate('/me/support'); }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
                >
                  <Headset className="h-3.5 w-3.5" />
                  Report a problem
                </button>
              </div>
            )}

            {/* ── Renderers ── */}
            {!loading && !error && doc && (
              <>
                {/* PDF — native browser engine via authenticated blob URL.
                    Using <embed> not <iframe sandbox> because Chrome's built-in PDF
                    renderer cannot initialise inside a sandboxed iframe.
                    The blob URL is already origin-isolated — no cookies, no localStorage,
                    no cross-origin requests are possible from raw PDF bytes. */}
                {isPdf && (
                  <embed
                    src={doc.blobUrl}
                    type="application/pdf"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                    title={doc.name}
                  />
                )}

                {/* DOCX — sanitized with DOMPurify; external fonts/resources blocked.
                    ignoreFonts + useBase64URL in docx-preview prevents network requests.
                    DOMPurify removes event handlers, scripts, dangerous attrs. */}
                {isDocx && doc.sanitizedHtml !== undefined && (
                  <div ref={viewRef} className="h-full overflow-auto bg-muted" style={{ zoom }}>
                    {/* Inject extracted docx-preview styles into a scoped style block */}
                    {doc.docxStyles && (
                      <div dangerouslySetInnerHTML={{ __html: doc.docxStyles }} />
                    )}
                    <style>{`
                      /* "> *" rather than ".docx-wrapper": the wrapper arrives as
                         ".docx-content-wrapper" here, so every rule below was dead — no grey
                         backdrop, no gap between pages, no page shadow, and no "safe center",
                         which is the part that keeps a zoomed page reachable on its left.
                         (No backticks in here: this block is a JS template literal.) */
                      .docx-content-wrap > * {
                        display:flex; flex-direction:column; align-items:center;
                        /* "safe center" keeps the page centred while it fits and falls back to
                           flush-left the moment it does not, so a zoomed-in page can still be
                           scrolled to its left margin. Ignored by browsers that lack it, which
                           keeps the plain centring above. */
                        align-items:safe center;
                        padding:24px 16px; gap:20px; min-height:100%;
                        background:#d1d5db;
                      }
                      /* The page is scaled to fit on a phone, so 24px of grey border around it
                         is 24px of the little width there is. */
                      @media (max-width: 640px) {
                        .docx-content-wrap > * { padding:10px 6px; gap:12px; }
                      }
                      .docx-content-wrap section {
                        box-shadow:0 2px 12px rgba(0,0,0,.18);
                        background:#fff;
                      }
                    `}</style>
                    <div
                      className="docx-content-wrap"
                      dangerouslySetInnerHTML={{ __html: doc.sanitizedHtml }}
                    />
                  </div>
                )}

                {/* Image */}
                {isImage && (
                  <div className="flex h-full items-center justify-center overflow-auto bg-muted p-8">
                    <img src={doc.blobUrl} alt={doc.name}
                      style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform .15s', maxWidth: '100%' }}
                      className="rounded shadow-lg" />
                  </div>
                )}

                {/* Other — download only */}
                {doc.kind === 'other' && (
                  <div className="flex h-full flex-col items-center justify-center gap-5">
                    <FileText className="h-14 w-14 text-muted-foreground/40" weight="duotone" />
                    <div className="text-center">
                      <p className="text-sm font-semibold text-foreground">{doc.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">This file type cannot be previewed.</p>
                    </div>
                    <a href={doc.blobUrl} download={doc.name}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                      <DownloadSimple className="h-4 w-4" /> Download to view
                    </a>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Version history sidebar ── */}
          {showHistory && documentId && (
            <div className="flex w-56 shrink-0 flex-col border-l border-border bg-card">
              <div className="border-b border-border px-4 py-2.5">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Version History
                </p>
              </div>
              <VersionPanel documentId={documentId} currentVersion={doc?.version} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
